import { Sparkles, Loader2 } from 'lucide-react';
import { useState, useEffect } from 'react';

const statusMessages = [
  "Analyzing your business data...",
  "Reviewing your pipeline...",
  "Looking for opportunities...",
  "Checking your tasks...",
  "Preparing insights...",
  "Evaluating priorities...",
];

const tips = [
  "Tip: Ask me to schedule follow-ups",
  "Tip: I can analyze your overdue invoices",
  "Tip: Try 'What should I focus on today?'",
  "Tip: Ask about stuck deals in your pipeline",
  "Tip: I can help prioritize your tasks",
];

interface StreamingIndicatorProps {
  startTime?: number;
}

export function StreamingIndicator({ startTime }: StreamingIndicatorProps) {
  const [messageIndex, setMessageIndex] = useState(0);
  const [tipIndex, setTipIndex] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [showTip, setShowTip] = useState(false);

  // Rotate status messages every 2.5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % statusMessages.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  // Show tip after 5 seconds, rotate every 4 seconds
  useEffect(() => {
    const tipTimer = setTimeout(() => {
      setShowTip(true);
    }, 5000);

    const tipRotate = setInterval(() => {
      setTipIndex((prev) => (prev + 1) % tips.length);
    }, 4000);

    return () => {
      clearTimeout(tipTimer);
      clearInterval(tipRotate);
    };
  }, []);

  // Track elapsed time
  useEffect(() => {
    const start = startTime || Date.now();
    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [startTime]);

  const isLongWait = elapsedSeconds > 15;

  return (
    <div className="flex gap-2">
      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
        <Sparkles className="h-4 w-4 text-primary animate-pulse" />
      </div>
      <div className="bg-muted rounded-lg px-3 py-2 max-w-[280px]">
        {/* Status message */}
        <div className="flex items-center gap-2 mb-1">
          <Loader2 className="h-3 w-3 text-primary animate-spin" />
          <span className="text-sm text-foreground animate-in fade-in duration-300" key={messageIndex}>
            {statusMessages[messageIndex]}
          </span>
        </div>
        
        {/* Progress bar */}
        <div className="h-1 bg-muted-foreground/20 rounded-full overflow-hidden mb-2">
          <div 
            className="h-full bg-primary/40 rounded-full animate-pulse"
            style={{ 
              width: isLongWait ? '85%' : `${Math.min(80, (elapsedSeconds / 20) * 100)}%`,
              transition: 'width 1s ease-out'
            }}
          />
        </div>

        {/* Tip or long wait message */}
        {isLongWait ? (
          <p className="text-xs text-amber-600 dark:text-amber-400 animate-in fade-in">
            This is taking longer than usual. Complex queries may need more time.
          </p>
        ) : showTip ? (
          <p className="text-xs text-muted-foreground animate-in fade-in duration-500" key={tipIndex}>
            {tips[tipIndex]}
          </p>
        ) : null}
      </div>
    </div>
  );
}
